import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";

const DISPOSITIONS = [
  { value: "completed", label: "Spoke with patient" },
  { value: "voicemail", label: "Left voicemail" },
  { value: "no_answer", label: "No answer" },
  { value: "wrong_number", label: "Wrong number" },
  { value: "callback", label: "Patient will call back" },
];

/**
 * Post-dial disposition modal. Pops after the click-to-call hands off, lets
 * staff record what happened on the call. Saves to /api/calls/{id}/note.
 */
export default function CallNoteDialog({ open, onClose, callId, patientName, dialedNumber, initialNote = "", initialDisposition = "" }) {
  const [note, setNote] = useState(initialNote);
  const [disposition, setDisposition] = useState(initialDisposition);
  const [saving, setSaving] = useState(false);

  const save = async (skip = false) => {
    if (!callId) { onClose(); return; }
    if (skip) { onClose(); return; }
    setSaving(true);
    try {
      await API.patch(`/calls/${encodeURIComponent(callId)}/note`, { note, disposition: disposition || null });
      toast.success("Call note saved");
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="call-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-purple-600" />
            <span>Log call disposition</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 text-xs">
            <p className="text-slate-500">Calling</p>
            <p className="font-semibold text-slate-900">{patientName || "Patient"}</p>
            {dialedNumber && <p className="font-mono text-slate-600 mt-1">{dialedNumber}</p>}
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600 mb-2 block">Outcome</Label>
            <div className="grid grid-cols-2 gap-2">
              {DISPOSITIONS.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDisposition(d.value)}
                  className={`text-xs px-3 py-2 rounded-xl border text-left transition-colors ${
                    disposition === d.value
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                  }`}
                  data-testid={`call-disposition-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="call-note-text" className="text-xs font-medium text-slate-600">Notes</Label>
            <Textarea
              id="call-note-text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What was discussed? (e.g., reviewed dosage adjustment to 0.5mg, no side-effects reported)"
              className="mt-1 rounded-xl"
              rows={4}
              data-testid="call-note-textarea"
            />
            <p className="text-[11px] text-slate-400 mt-1">Saved to the patient&apos;s audit log. Visible to all staff.</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => save(true)} disabled={saving} data-testid="call-note-skip">Skip</Button>
          <Button onClick={() => save(false)} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="call-note-save">
            {saving ? "Saving…" : "Save Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
